import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Globe, Loader2 } from 'lucide-react';

export interface Slot {
  startAt: string;
  endAt: string;
}

interface SlotPickerProps {
  token: string;
  expiresAt?: string | null;
  selected: Slot | null;
  onSelect: (slot: Slot) => void;
  // Horário a esconder (ex.: o atual, na remarcação)
  excludeStartAt?: string;
}

const DAY_MS = 86400000;

// Data civil 'YYYY-MM-DD' no fuso do navegador
export const localDayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
};

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);

const SlotPicker: React.FC<SlotPickerProps> = ({ token, expiresAt, selected, onSelect, excludeStartAt }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'pt';
  const today = useMemo(() => new Date(), []);
  const [month, setMonth] = useState<Date>(startOfMonth(today));
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [dayKey, setDayKey] = useState<string | null>(selected ? localDayKey(new Date(selected.startAt)) : null);
  const [nextAvailable, setNextAvailable] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [noSlotsAtAll, setNoSlotsAtAll] = useState(false);

  const lastMonth = useMemo(() => (expiresAt ? startOfMonth(new Date(expiresAt)) : addMonths(today, 12)), [expiresAt, today]);

  const filterSlots = useCallback(
    (list: Slot[]) => (excludeStartAt ? list.filter((s) => new Date(s.startAt).getTime() !== new Date(excludeStartAt).getTime()) : list),
    [excludeStartAt]
  );

  // Primeira carga: procura o primeiro dia com vaga (até ~2 meses) e abre o calendário nesse mês
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const from = new Date();
        const to = new Date(from.getTime() + 62 * DAY_MS);
        const res = await axios.get(`/agendamento/public/${token}/slots`, {
          params: { from: from.toISOString(), to: to.toISOString() }
        });
        if (cancelled) return;
        const list = filterSlots(res.data.slots || []);
        if (list.length === 0) {
          setNoSlotsAtAll(true);
        } else if (!selected) {
          const first = new Date(list[0].startAt);
          setMonth(startOfMonth(first));
          setDayKey(localDayKey(first));
        } else {
          setMonth(startOfMonth(new Date(selected.startAt)));
        }
      } catch {
        // falha de rede: o carregamento do mês abaixo mostra o estado vazio
      } finally {
        if (!cancelled) setInitialized(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Horários do mês visível (+ próximo dia com vaga, para o atalho quando o mês está vazio)
  useEffect(() => {
    if (!initialized) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const from = new Date(Math.max(month.getTime(), Date.now()));
        const to = addMonths(month, 1);
        const res = await axios.get(`/agendamento/public/${token}/slots`, {
          params: { from: from.toISOString(), to: to.toISOString() }
        });
        if (cancelled) return;
        const list = filterSlots(res.data.slots || []);
        setSlots(list);
        setNextAvailable(null);
        if (list.length === 0 && to <= lastMonth) {
          const ahead = await axios.get(`/agendamento/public/${token}/slots`, {
            params: { from: to.toISOString(), to: new Date(to.getTime() + 62 * DAY_MS).toISOString() }
          });
          const aheadList = filterSlots(ahead.data.slots || []);
          if (!cancelled && aheadList.length > 0) setNextAvailable(aheadList[0].startAt);
        }
      } catch {
        if (!cancelled) setSlots([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, month, initialized, lastMonth, filterSlots]);

  const slotsByDay = useMemo(() => {
    const map = new Map<string, Slot[]>();
    for (const s of slots) {
      const key = localDayKey(new Date(s.startAt));
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    }
    return map;
  }, [slots]);

  // Grade do mês começando na segunda-feira
  const cells = useMemo(() => {
    const first = startOfMonth(month);
    const lead = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const list: (Date | null)[] = Array.from({ length: lead }, () => null);
    for (let d = 1; d <= daysInMonth; d += 1) list.push(new Date(month.getFullYear(), month.getMonth(), d));
    while (list.length % 7 !== 0) list.push(null);
    return list;
  }, [month]);

  const weekdayLabels = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(lang, { weekday: 'short' });
    // 2024-01-01 foi segunda-feira
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 1 + i)).replace('.', ''));
  }, [lang]);

  const monthLabel = new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' }).format(month);
  const timeFmt = useMemo(() => new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit' }), [lang]);
  const dayTitleFmt = useMemo(() => new Intl.DateTimeFormat(lang, { weekday: 'long', day: 'numeric', month: 'long' }), [lang]);

  const canPrev = month > startOfMonth(today);
  const canNext = month < lastMonth;
  const daySlots = dayKey ? slotsByDay.get(dayKey) || [] : [];
  const tz = browserTimezone();

  const goToNextAvailable = () => {
    if (!nextAvailable) return;
    const d = new Date(nextAvailable);
    setMonth(startOfMonth(d));
    setDayKey(localDayKey(d));
  };

  if (!initialized) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-7 h-7 animate-spin text-primary-600" aria-label={t('common.loading') as string} />
      </div>
    );
  }

  if (noSlotsAtAll) {
    return <p className="text-center text-gray-600 py-12 px-4">{t('agendamento.noSlotsAtAll')}</p>;
  }

  return (
    <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,220px)]">
      <div>
        <h3 className="text-base font-semibold text-gray-900 mb-3">{t('agendamento.pickDate')}</h3>
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => setMonth(addMonths(month, -1))}
            disabled={!canPrev}
            className="p-2 rounded-full text-primary-700 hover:bg-primary-50 disabled:text-gray-300 disabled:hover:bg-transparent"
            aria-label={t('agendamento.prevMonth') as string}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="font-medium text-gray-900 capitalize">{monthLabel}</span>
          <button
            type="button"
            onClick={() => setMonth(addMonths(month, 1))}
            disabled={!canNext}
            className="p-2 rounded-full text-primary-700 hover:bg-primary-50 disabled:text-gray-300 disabled:hover:bg-transparent"
            aria-label={t('agendamento.nextMonth') as string}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium uppercase tracking-wide text-gray-500 mb-1">
          {weekdayLabels.map((w) => (
            <span key={w}>{w}</span>
          ))}
        </div>
        <div className={`grid grid-cols-7 gap-1 ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
          {cells.map((d, i) => {
            if (!d) return <span key={`e${i}`} />;
            const key = localDayKey(d);
            const available = slotsByDay.has(key);
            const isSelected = key === dayKey;
            const isToday = key === localDayKey(today);
            return (
              <button
                key={key}
                type="button"
                disabled={!available}
                onClick={() => setDayKey(key)}
                aria-pressed={isSelected}
                className={`relative aspect-square max-h-12 w-full rounded-full text-sm transition-colors ${
                  isSelected
                    ? 'bg-primary-600 text-white font-semibold'
                    : available
                      ? 'bg-primary-50 text-primary-700 font-semibold hover:bg-primary-100'
                      : 'text-gray-300 cursor-default'
                }`}
              >
                {d.getDate()}
                {isToday && (
                  <span
                    className={`absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full ${isSelected ? 'bg-white' : 'bg-primary-600'}`}
                  />
                )}
              </button>
            );
          })}
        </div>

        {!loading && slots.length === 0 && (
          <div className="mt-4 text-center text-sm text-gray-600">
            <p>{t('agendamento.noSlotsMonth')}</p>
            {nextAvailable && (
              <button type="button" onClick={goToNextAvailable} className="mt-2 text-primary-700 font-medium hover:underline">
                {t('agendamento.nextAvailable')}
              </button>
            )}
          </div>
        )}

        {tz && (
          <p className="mt-4 flex items-center gap-1.5 text-xs text-gray-500">
            <Globe className="w-3.5 h-3.5 shrink-0" />
            {t('agendamento.timezoneNote', { tz: tz.replace(/_/g, ' ') })}
          </p>
        )}
      </div>

      <div className="min-w-0">
        <h3 className="text-base font-semibold text-gray-900 mb-3 first-letter:uppercase">
          {dayKey ? dayTitleFmt.format(new Date(`${dayKey}T12:00:00`)) : t('agendamento.pickTime')}
        </h3>
        {dayKey && daySlots.length === 0 && !loading && <p className="text-sm text-gray-500">{t('agendamento.noSlotsDay')}</p>}
        <div className="grid grid-cols-2 md:grid-cols-1 gap-2 md:max-h-[22rem] md:overflow-y-auto md:pr-1">
          {daySlots.map((s) => {
            const isSel = selected?.startAt === s.startAt;
            return (
              <button
                key={s.startAt}
                type="button"
                onClick={() => onSelect(s)}
                className={`w-full py-2.5 rounded-lg border text-sm font-semibold transition-colors ${
                  isSel
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : 'border-primary-300 text-primary-700 hover:border-primary-600 hover:bg-primary-50'
                }`}
              >
                {timeFmt.format(new Date(s.startAt))}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SlotPicker;
